import { Visual } from '@/modules/Visual/Index';
import { forwardRef } from 'react';
import { InputProps, InputRef } from '@/shared/types/input';
import { cn } from '@/shared/utils/cn';
import { useControlledPrimitive } from '@/shared/hooks/useControlledState';
import { useInputSlots, useInputRef, useInputStyles } from '../hooks';
import { Button } from '@/modules/Button';
import { Icon } from '@/modules/Icon';
import { IconType, VisualSlotType } from '@/shared/types/types';
import { faInfoCircle, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { isValidUrl } from '@/modules/Avatar/utils';

/**
 * Input - Componente de campo de entrada versátil y configurable
 *
 * Componente de input que soporta estados controlados y no controlados, labels, mensajes de error,
 * iconos, slots personalizados, tooltips informativos y múltiples tamaños.
 *
 * @example
 * ```tsx
 * // Input simple
 * <Input
 *   label="Nombre"
 *   value={name}
 *   onValueChange={(value) => setName(value)}
 * />
 *
 * // Input con icono izquierdo
 * <Input
 *   label="Email"
 *   leftSlot="EnvelopeOutlined"
 *   value={email}
 *   onValueChange={(value) => setEmail(value)}
 * />
 *
 * // Input con error
 * <Input
 *   label="Contraseña"
 *   error="La contraseña debe tener al menos 8 caracteres"
 *   value={password}
 *   onValueChange={(value) => setPassword(value)}
 * />
 *
 * // Sin ancho mínimo del control (contenedor estrecho, `fullWidth` false)
 * <Input
 *   label="Buscar"
 *   fullWidth={false}
 *   minWidthEnabled={false}
 *   value={q}
 *   onValueChange={setQ}
 * />
 * ```
 *
 * @see {@link InputProps} para la lista completa de props
 */
export const Input = forwardRef<InputRef, InputProps>(
  (
    {
      label = '',
      error = '',
      helperText,
      size = 'sm',
      color,
      leftSlot,
      rightSlot = null,
      extraSlot = null,
      leftSlotClassName,
      caret = true,
      errorCursor = false,
      value,
      fullWidth = false,
      minWidthEnabled = true,
      defaultValue = '',
      disabled = false,
      onValueChange,
      onChange,
      onClickExtraSlot,
      className,
      style,
      name,
      autosize = false,
      colorLeftIcon,
      colorRightIcon,
      colorExtraIcon,
      leftSlotDuotonePrimary,
      leftSlotDuotoneSecondary,
      leftSlotDuotoneOpacityPrimary,
      leftSlotDuotoneOpacitySecondary,
      rightSlotDuotonePrimary,
      rightSlotDuotoneSecondary,
      rightSlotDuotoneOpacityPrimary,
      rightSlotDuotoneOpacitySecondary,
      extraSlotDuotonePrimary,
      extraSlotDuotoneSecondary,
      extraSlotDuotoneOpacityPrimary,
      extraSlotDuotoneOpacitySecondary,
      infoTooltip = '',
      pill = 'sm',
      clearable = false,
      onClear,
      ...props
    },
    ref
  ) => {
    // Usar el hook de estado controlado
    const {
      value: currentValue,
      setValue,
      isControlled,
    } = useControlledPrimitive({
      value: value as string,
      defaultValue: defaultValue as string,
      onChange: onValueChange,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      // Callbacks
      onChange?.(e);
    };

    const hasClearSlot = Boolean(
      clearable && onClear && !disabled && String(currentValue ?? '').length > 0
    );

    // Hook para manejar slots e iconos
    const { hasRightSlot, hasExtraSlot, paddingClasses, getLeftSlotPosition } = useInputSlots({
      size,
      leftSlot: leftSlot ?? undefined,
      rightSlot: rightSlot ?? undefined,
      extraSlot: extraSlot ?? undefined,
      hasClearSlot,
    });

    const showTrailingRail = Boolean(rightSlot || hasClearSlot || extraSlot);
    const trailingRailRightClass = size === 'lg' ? 'ui-right-3.5' : 'ui-right-2.5';

    // Hook para manejar estilos
    const { baseClasses, containerClasses, labelClasses } = useInputStyles({
      size,
      error,
      disabled,
      fullWidth,
      minWidthEnabled,
      caret,
      errorCursor,
      paddingClasses,
      autosize,
      pill,
    });

    // Hook para manejar referencias
    useInputRef({
      ref,
      currentValue,
      setValue,
      isControlled,
      inputId: props.id,
    });

    // Función helper para detectar si el leftSlot es una URL
    const isLeftSlotUrl = (slot: VisualSlotType | null | undefined): boolean => {
      if (!slot || typeof slot !== 'string') return false;
      if (slot.startsWith('data:') || slot.startsWith('blob:')) return true;
      return isValidUrl(slot);
    };

    const leftSlotIsUrl = isLeftSlotUrl(leftSlot);

    return (
      <div
        className={cn(
          'ui-imj ui-relative ui-flex ui-flex-col ui-gap-1',
          fullWidth ? 'ui-w-full' : 'ui-w-fit',
          className
        )}
        style={style}
      >
        {(label || infoTooltip) && (
          <div className="ui-flex ui-flex-row ui-items-center ui-gap-1 ui-leading-tight">
            {label && (
              <label htmlFor={props.id} className={labelClasses}>
                {label}
              </label>
            )}
            {infoTooltip && (
              <Button
                theme="solid"
                size="xs"
                color="tertiary"
                as="div"
                tooltip={infoTooltip}
                clickable={false}
                pointer={false}
                icon={faInfoCircle}
              />
            )}
          </div>
        )}
        <div className={containerClasses}>
          {leftSlot && (
            <span
              className={cn(
                `ui-absolute ${getLeftSlotPosition} ui-top-1/2 ui--translate-y-1/2`,
                'ui-inline-flex ui-leading-[0] ui-transition-colors ui-duration-150',
                colorLeftIcon
                  ? ''
                  : disabled
                    ? 'ui-text-disabled'
                    : error
                      ? 'ui-text-danger'
                      : 'ui-text-tertiary group-focus-within:ui-text-brand'
              )}
            >
              {leftSlotIsUrl ? (
                <Visual
                  slot={leftSlot}
                  size="xxs"
                  defaultSizeIcon={false}
                  colorIcon={colorLeftIcon}
                  colorIconDuotonePrimary={leftSlotDuotonePrimary}
                  colorIconDuotoneSecondary={leftSlotDuotoneSecondary}
                  opacityIconDuotonePrimary={leftSlotDuotoneOpacityPrimary}
                  opacityIconDuotoneSecondary={leftSlotDuotoneOpacitySecondary}
                  className={leftSlotClassName}
                />
              ) : (
                <Icon
                  name={leftSlot as IconType}
                  size="sm"
                  color={colorLeftIcon || 'current'}
                  colorDuotonePrimary={leftSlotDuotonePrimary}
                  colorDuotoneSecondary={leftSlotDuotoneSecondary}
                  opacityDuotonePrimary={leftSlotDuotoneOpacityPrimary}
                  opacityDuotoneSecondary={leftSlotDuotoneOpacitySecondary}
                  className={leftSlotClassName}
                />
              )}
            </span>
          )}

          <input
            placeholder="Placeholder"
            disabled={disabled}
            value={currentValue}
            onChange={handleChange}
            name={name}
            data-input-id={props.id || 'default-input'}
            style={
              errorCursor
                ? {
                    caretColor: '#ef4444',
                    borderColor: 'var(--ui-color-border-danger, #ef4444)',
                    outline: 'none',
                  }
                : undefined
            }
            {...props}
            className={cn(baseClasses, 'ui-min-w-12')}
          />
          {showTrailingRail && (
            <div
              className={cn(
                'ui-pointer-events-none ui-absolute ui-top-1/2 ui-z-[4] ui-flex ui--translate-y-1/2 ui-items-center ui-gap-x-8',
                trailingRailRightClass
              )}
            >
              {rightSlot && (
                <span
                  className={cn(
                    'ui-pointer-events-none ui-inline-flex ui-leading-[0] ui-transition-colors ui-duration-150',
                    colorRightIcon
                      ? ''
                      : disabled
                        ? 'ui-text-disabled'
                        : error
                          ? 'ui-text-danger'
                          : 'ui-text-tertiary group-focus-within:ui-text-brand'
                  )}
                >
                  <Icon
                    name={rightSlot as IconType}
                    size="sm"
                    color={colorRightIcon || 'current'}
                    colorDuotonePrimary={rightSlotDuotonePrimary}
                    colorDuotoneSecondary={rightSlotDuotoneSecondary}
                    opacityDuotonePrimary={rightSlotDuotoneOpacityPrimary}
                    opacityDuotoneSecondary={rightSlotDuotoneOpacitySecondary}
                  />
                </span>
              )}
              {hasRightSlot && (hasClearSlot || hasExtraSlot) && (
                <div
                  className={`ui-h-5 ui-w-[1px] ui-min-w-[1px] ui-shrink-0 ${error ? 'ui-bg-danger' : 'ui-bg-[var(--ui-color-border-default)]'}`}
                  aria-hidden
                />
              )}
              {hasClearSlot && (
                <button
                  type="button"
                  aria-label="Limpiar"
                  data-dropdown-clear-trigger
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear?.(e);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="ui-pointer-events-auto ui-flex ui-place-items-center ui-rounded-control-sm ui-text-tertiary ui-transition-colors hover:ui-text-brand group-focus-within:ui-text-brand"
                >
                  <Icon name={faXmark} size="sm" color="current" />
                </button>
              )}
              {hasClearSlot && hasExtraSlot && (
                <div
                  className={`ui-h-5 ui-w-[1px] ui-min-w-[1px] ui-shrink-0 ${error ? 'ui-bg-danger' : 'ui-bg-[var(--ui-color-border-default)]'}`}
                  aria-hidden
                />
              )}
              {extraSlot && (
                <button
                  onClick={onClickExtraSlot}
                  disabled={disabled}
                  type="button"
                  className={cn(
                    'ui-flex ui-place-items-center ui-transition-colors',
                    disabled
                      ? 'ui-cursor-not-allowed ui-text-disabled disabled:hover:ui-text-disabled disabled:active:ui-text-disabled'
                      : 'ui-pointer-events-auto ui-text-tertiary hover:ui-text-brand group-focus-within:ui-text-brand'
                  )}
                >
                  <Icon
                    name={extraSlot as IconType}
                    size="sm"
                    color={colorExtraIcon || 'current'}
                    colorDuotonePrimary={extraSlotDuotonePrimary}
                    colorDuotoneSecondary={extraSlotDuotoneSecondary}
                    opacityDuotonePrimary={extraSlotDuotoneOpacityPrimary}
                    opacityDuotoneSecondary={extraSlotDuotoneOpacitySecondary}
                  />
                </button>
              )}
            </div>
          )}
        </div>
        {error && (
          <p className="ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-danger">
            {error}
          </p>
        )}
        {helperText && (
          <p className="ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-tertiary">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
